/**
 * @fileoverview Minimal plug-and-play accordion custom element
 *
 * @example
 * // Basic usage
 * <accordion-element>
 *   <accordion-summary>
 *     <h3>Click to expand</h3>
 *   </accordion-summary>
 *   <accordion-content>
 *     <p>Hidden content here</p>
 *   </accordion-content>
 * </accordion-element>
 *
 * @example
 * // Default open
 * <accordion-element open>
 *   <accordion-summary>
 *     <h3>Already expanded</h3>
 *   </accordion-summary>
 *   <accordion-content>
 *     <p>Visible content here</p>
 *   </accordion-content>
 * </accordion-element>
 *
 * @example
 * // Programmatic control via window events
 * window.dispatchEvent(new Event('open-accordion-my-accordion'));
 * window.dispatchEvent(new Event('close-accordion-my-accordion'));
 * window.dispatchEvent(new Event('toggle-accordion-my-accordion'));
 */

const ACCORDION_STYLES = `
  :host {
    display: block;
  }

  .accordion-content {
    display: grid;
    grid-template-rows: 0fr;
    overflow: hidden;
    transition: grid-template-rows var(--accordion-transition-duration, 0.3s) ease;
  }

  :host([open]) .accordion-content {
    grid-template-rows: 1fr;
  }

  .accordion-content__inner {
    min-height: 0;
    overflow: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    .accordion-content {
      transition: none;
    }
  }
`;

class AccordionElement extends HTMLElement {
  /** @type {HTMLElement|null} */
  #content = null;
  #boundToggle = null;
  #boundOpen = null;
  #boundClose = null;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.innerHTML = `
      <style>${ACCORDION_STYLES}</style>
      <slot name="summary"></slot>
      <div class="accordion-content">
        <div class="accordion-content__inner">
          <slot name="content"></slot>
        </div>
      </div>
    `;

    // Bind methods for proper cleanup
    this.#boundToggle = () => this.toggle();
    this.#boundOpen = () => this.open();
    this.#boundClose = () => this.close();
  }

  connectedCallback() {
    this.#content = this.shadowRoot.querySelector('.accordion-content');

    // Listen on the shadow DOM slot — works regardless of child parse timing
    const summarySlot = this.shadowRoot.querySelector('slot[name="summary"]');
    summarySlot?.addEventListener('click', this.#boundToggle);

    // Set ARIA state once the summary element gets slotted
    summarySlot?.addEventListener('slotchange', () => {
      const summary = this.querySelector('accordion-summary');
      if (summary) {
        summary.setAttribute('aria-expanded', this.hasAttribute('open') ? 'true' : 'false');
      }
    });

    // Window events for external control
    const id = this.id;
    if (id) {
      window.addEventListener(`open-accordion-${id}`, this.#boundOpen);
      window.addEventListener(`close-accordion-${id}`, this.#boundClose);
      window.addEventListener(`toggle-accordion-${id}`, this.#boundToggle);
    }
  }

  disconnectedCallback() {
    const id = this.id;
    if (id) {
      window.removeEventListener(`open-accordion-${id}`, this.#boundOpen);
      window.removeEventListener(`close-accordion-${id}`, this.#boundClose);
      window.removeEventListener(`toggle-accordion-${id}`, this.#boundToggle);
    }
  }

  /** Opens the accordion */
  open() {
    this.setAttribute('open', '');
    this.querySelector('accordion-summary')?.setAttribute('aria-expanded', 'true');
    this.dispatchEvent(new CustomEvent('accordion:open', { bubbles: true }));
  }

  /** Closes the accordion */
  close() {
    this.removeAttribute('open');
    this.querySelector('accordion-summary')?.setAttribute('aria-expanded', 'false');
    this.dispatchEvent(new CustomEvent('accordion:close', { bubbles: true }));
  }

  /** Toggles the accordion */
  toggle() {
    if (this.hasAttribute('open')) {
      this.close();
    } else {
      this.open();
    }
  }

  /** @returns {boolean} Whether the accordion is open */
  get isOpen() {
    return this.hasAttribute('open');
  }
}

class AccordionSummary extends HTMLElement {
  connectedCallback() {
    this.setAttribute('slot', 'summary');
    this.setAttribute('role', 'button');
    this.setAttribute('tabindex', '0');
    this.style.cursor = 'pointer';

    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.click();
      }
    });
  }
}

class AccordionContent extends HTMLElement {
  connectedCallback() {
    this.setAttribute('slot', 'content');
  }
}

customElements.define('accordion-element', AccordionElement);
customElements.define('accordion-summary', AccordionSummary);
customElements.define('accordion-content', AccordionContent);
